import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { OrganizationType } from "../api/graphql";
import { useServerEvent } from "../lib/serverEventsProvider";
import { useCurrentCorrespondent } from "../SessionBoundary";
import CallScreen from "./CallScreen.tsx";
import { BlankDialingScreen } from "./DialingScreen";
import { GetCallInfoDocument } from "./GetCallInfo.generated.ts";
import { useCallPolling } from "./useCallPolling.ts";

export default function VoiceCallScreen() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentCorrespondent();

  if (!id) throw new Error("Missing callId");

  const { data, loading, error, refetch } = useQuery(GetCallInfoDocument, {
    fetchPolicy: "no-cache", // do not cache access token
    variables: { callId: id },
  });

  const call = data?.call.call;
  const currentParticipant = call?.participants.find(
    (p) => p.externalId === user.id
  );

  useServerEvent("newVoiceCall", ({ callId: newCallId }) => {
    if (call && newCallId === call.id) refetch();
  });

  // poll for meeting information. mostly useful for on-demand meetings but
  // also for incidents where CVH doesn't start on time.
  useCallPolling({ call, refetch, intervalMs: 10000 });

  if (loading || !call || !currentParticipant) return <BlankDialingScreen />;

  if (error) throw error;

  return (
    <CallScreen
      allowRating
      allowTextFeedback={
        user.__typename === "Visitor" &&
        // Healthcare org members cannot leave feedback
        // because of HIPAA regulations
        !user.organizationMemberships?.some(
          (om) => om.organization.type === OrganizationType.Healthcare
        )
      }
      call={call}
      currentParticipant={currentParticipant}
    />
  );
}
